import React, { useRef, useState, useEffect } from "react";
import {
  Platform,
  UIManager,
  findNodeHandle,
  TouchableOpacity,
  TouchableHighlight,
  TouchableWithoutFeedback,
} from "react-native";
import type { GuideResponseProps } from "../types";
import { Button, Text, View } from "../Components/Themed";
import { useColors } from "../constants/useColors";
import { Icons } from "../Components";

export type GuideWrapperProps = {
  children: React.ReactNode;
  active?:boolean,
  prompt_location?:'bottom'|'top',
  guide?: GuideResponseProps;
  onComplete: (status:'cancelled'|'complete') => void
};

type Layout = { x: number; y: number; width: number; height: number };

export default function GuideWrapper({ active, prompt_location, children, guide, onComplete }: GuideWrapperProps) {
  const Colors = useColors();
  const wrapper_guid:GuideResponseProps = guide ?? { view_type: 'prompt' };
  const { tour_prompts } = wrapper_guid;
  const tour = tour_prompts ?? []
  const refs = useRef<{ [key: string]: any }>({});
  const containerRef = useRef<any>(null);

  const [currentStep, setCurrentStep] = useState(0);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [containerLayout, setContainerLayout] = useState<Layout>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [tooltipHeight, setTooltipHeight] = useState(0);

  const sortedTour = tour
    ? [...tour].sort((a, b) => (a.tour_priority ?? 0) - (b.tour_priority ?? 0))
    : [];
  const step = sortedTour[currentStep];
  const stepRef = step ? refs.current[step.client_native_id] : undefined;

  const isTouchable = (child: any) => {
    const typeName = (child.type as any)?.displayName || child.type;
    return (
      child.type === TouchableOpacity ||
      child.type === TouchableHighlight ||
      child.type === TouchableWithoutFeedback ||
      (typeof typeName === "string" && typeName.startsWith("Touchable"))
    );
  };

  const handleDone = (status:'cancelled'|'complete') => {
    setCurrentStep(0);
    if(onComplete){ onComplete(status) }
  }


  // Recursively attach refs to children with nativeID
  const attachRefs = (nodes: React.ReactNode): React.ReactNode => {
    return React.Children.map(nodes, (child) => {
      if (!React.isValidElement(child)) return child;

      const props = child.props as { nativeID?: string; children?: React.ReactNode };
      const nativeID = props.nativeID;
      const newChildren = props.children ? attachRefs(props.children) : props.children;

      if (nativeID) {
        if (isTouchable(child)) {
          return (
            <View
              ref={(r: any) => {
                if (r) refs.current[nativeID] = r;
              }}
              style={{ flex: 0 }}
            >
              {React.cloneElement(child as React.ReactElement<any>, { children: newChildren } as any)}
            </View>
          );
        }

        return React.cloneElement(child as React.ReactElement<any>, {
          ref: (r: any) => {
            if (r) refs.current[nativeID] = r;
          },
          children: newChildren,
        } as any);
      } else if (newChildren !== props.children) {
        return React.cloneElement(child as React.ReactElement<any>, { children: newChildren } as any);
      }

      return child;
    });
  };

  // Efficient measuring: only runs when tour is active
  useEffect(() => {
    if (!active || !step) return;

    const measureStep = () => {
      if (!stepRef || !containerRef.current) return;

      if (Platform.OS === "web") {
        const containerRect = containerRef.current.getBoundingClientRect();
        const rect = stepRef.getBoundingClientRect();
        setLayout({
          x: rect.left - containerRect.left,
          y: rect.top - containerRect.top,
          width: rect.width,
          height: rect.height,
        });
      } else {
        const handle = findNodeHandle(stepRef);
        const containerHandle = findNodeHandle(containerRef.current);
        if (!handle || !containerHandle) return;

        UIManager.measureLayout(
          handle,
          containerHandle,
          () => console.log("measureLayout failed"),
          (x: number, y: number, width: number, height: number) =>
            setLayout({ x, y, width, height })
        );
      }
    };

    // Ensure component is mounted
    setTimeout(measureStep, 0);

    // Web: re-measure on window resize
    if (Platform.OS === "web") {
      const resizeHandler = () => measureStep();
      window.addEventListener("resize", resizeHandler);
      return () => window.removeEventListener("resize", resizeHandler);
    }
    return
  }, [active, currentStep, step]);

  const handleNext = () => {
    if (currentStep + 1 < sortedTour.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDone('complete')
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (!tour) return <>{children}</>;

  if (!layout || !active || !step)
    return <View ref={containerRef}>{attachRefs(children)}</View>;

  const MIN_WIDTH = 300;
  const right = layout.x + MIN_WIDTH > containerLayout.width;
  const isLastStep = currentStep === sortedTour.length - 1;

  return (
    <View
      ref={containerRef}
      onLayout={(ev) => {
        const l = ev.nativeEvent.layout;
        setContainerLayout({
          x: l.x,
          y: l.y,
          width: l.width,
          height: l.height,
        });
      }}
      style={{ flex: 1 }}
    >
      {attachRefs(children)}

      {/* Highlight */}
      <View
        transparent
        pointerEvents="none"
        style={{
          position: "absolute",
          zIndex: 9999999,
          borderWidth: 2,
          borderColor: Colors.text.success,
          borderRadius: 4,
          top: layout.y - 5,
          left: layout.x - 5,
          width: layout.width + 10,
          height: layout.height + 10,
        }}
      />

      {/* Tooltip: always above overlay */}
      <View
        float
        onLayout={(ev) => setTooltipHeight(ev.nativeEvent.layout.height)}
        style={{
          position: "absolute",
          maxWidth: 330,
          minWidth: 225,
          borderColor: Colors.text.success,
          borderWidth: 1,
          top: prompt_location == 'bottom' ? layout.y + layout.height + 10 : layout.y - tooltipHeight - 10, // 10px margin above overlay
          left: right ? undefined : layout.x,
          right: right ? 5 : undefined,
          zIndex: 99999999,
        }}
      >
        {step.title && (
          <View type='header' style={{ flexDirection: "row", alignItems: "center", padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
            <Icons.AlertIcon size={14} color={Colors.text.warning} />
            <Text theme="h1" style={{ flex: 1, marginLeft: 10, marginRight: 10 }}>
              {step.title}
            </Text>
            <Button
              style={{
                height: 30,
                width: 30,
                borderRadius: 100,
                padding: 0,
                justifyContent: "center",
                alignItems: "center",
              }}
              title="X"
              type="error"
              onPress={() => handleDone('cancelled')}
            />
          </View>
        )}

        {step.body && (
          <View type='body' style={{ padding: 10 }}>
            <Text theme="h2">{step.body}</Text>
          </View>
        )}

        <View type='footer' style={{ flexDirection: "row", alignItems: "center", padding: 10, borderBottomRightRadius:8,borderBottomLeftRadius:8 }}>
          {currentStep > 0 && (
            <Button type="action" onPress={handlePrev} style={{ flex: 1, marginRight: 5 }} title="BACK" />
          )}
            <Button type="success" onPress={handleNext} style={{ flex: 1 }} title={isLastStep ? "Done" : "Next"} />
        </View>
      </View>
    </View>
  );
}
